import { storableError } from '../../util/errors';
import { getUserCartDetails } from '../../util/dataExtractor';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isArrayLength } from '../../util/genericHelpers';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { saveCartAndRedirectToLogin } from '../../util/cartHelpers';

const RESULT_PAGE_SIZE = 50;

// Action types
const ActionTypes = {
  FETCH_LISTING_REQUEST: 'app/CartPage/FETCH_LISTING_REQUEST',
  FETCH_LISTING_SUCCESS: 'app/CartPage/FETCH_LISTING_SUCCESS',
  FETCH_LISTING_ERROR: 'app/CartPage/FETCH_LISTING_ERROR',
  ADD_TO_CART_REQUEST: 'app/CartPage/ADD_TO_CART_REQUEST',
  ADD_TO_CART_SUCCESS: 'app/CartPage/ADD_TO_CART_SUCCESS',
  ADD_TO_CART_ERROR: 'app/CartPage/ADD_TO_CART_ERROR',
  REMOVE_ITEM_FROM_CART_REQUEST: 'app/CartPage/REMOVE_ITEM_FROM_CART_REQUEST',
  UNSET_REMOVING_ITEM_IN_PROGRESS: 'app/CartPage/UNSET_REMOVING_ITEM_IN_PROGRESS',
  REMOVE_ITEM_FROM_CART_SUCCESS: 'app/CartPage/REMOVE_ITEM_FROM_CART_SUCCESS',
  REMOVE_ITEM_FROM_CART_ERROR: 'app/CartPage/REMOVE_ITEM_FROM_CART_ERROR',
  UPDATE_CURRENT_PAGE_RESULT_IDS: 'app/CartPage/UPDATE_CURRENT_PAGE_RESULT_IDS',
  UPDATE_CART_COUNT: 'app/CartPage/UPDATE_CART_COUNT',
  SET_CART: 'app/CartPage/SET_CART',
};

// Initial state
const initialState = {
  cart: [],
  cartCount: 0,
  fetchListingInProgress: false,
  fetchListingError: null,
  currentPageResultIds: [],
  removeItemFromCartInProgress: false,
  removeItemFromCartError: null,
  currentCartItemId: null,
  addToCartInProgress: false,
  addToCartError: null,
};

// Reducer
const cartPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.ADD_TO_CART_REQUEST:
      return {
        ...state,
        currentCartItemId: payload,
        addToCartInProgress: true,
        addToCartError: null,
      };

    case ActionTypes.ADD_TO_CART_SUCCESS:
      return {
        ...state,
        addToCartInProgress: false,
        currentCartItemId: null,
        cart: state.cart.includes(payload) ? state.cart : [...state.cart, payload],
      };

    case ActionTypes.ADD_TO_CART_ERROR:
      return {
        ...state,
        addToCartInProgress: false,
        currentCartItemId: null,
        addToCartError: payload,
      };

    case ActionTypes.SET_CART:
      return { ...state, cart: payload };

    case ActionTypes.UPDATE_CART_COUNT:
      return { ...state, currentCartItemId: null, addToCartInProgress: false, cartCount: payload };

    case ActionTypes.FETCH_LISTING_REQUEST:
      return {
        ...state,
        fetchListingInProgress: true,
        currentPageResultIds: [],
        fetchListingError: null,
      };

    case ActionTypes.FETCH_LISTING_SUCCESS:
      return {
        ...state,
        currentPageResultIds: payload.data.data.map(l => l.id),
        pagination: payload.data.meta,
        fetchListingInProgress: false,
      };

    case ActionTypes.UPDATE_CURRENT_PAGE_RESULT_IDS:
      return { ...state, currentPageResultIds: payload };

    case ActionTypes.FETCH_LISTING_ERROR:
      return { ...state, fetchListingInProgress: false, fetchListingError: payload };

    case ActionTypes.REMOVE_ITEM_FROM_CART_REQUEST:
      return {
        ...state,
        removeItemFromCartInProgress: true,
        removeItemFromCartError: null,
        currentCartItemId: payload,
      };

    case ActionTypes.UNSET_REMOVING_ITEM_IN_PROGRESS:
      return {
        ...state,
        removeItemFromCartInProgress: true,
        removeItemFromCartError: null,
        currentCartItemId: null,
      };

    case ActionTypes.REMOVE_ITEM_FROM_CART_SUCCESS:
      return {
        ...state,
        removeItemFromCartInProgress: false,
        removeItemFromCartError: null,
        currentCartItemId: null,
      };

    case ActionTypes.REMOVE_ITEM_FROM_CART_ERROR:
      return {
        ...state,
        removeItemFromCartInProgress: false,
        removeItemFromCartError: payload,
        currentCartItemId: null,
      };

    default:
      return state;
  }
};

// Selectors
export const cartSelector = ({ CartPage }) => ({
  cart: CartPage.cart,
  cartCount: CartPage.cartCount,
  removeItemFromCartError: CartPage.removeItemFromCartError,
  addToCartInProgress: CartPage.addToCartInProgress,
  addToCartError: CartPage.addToCartError,
});
export const cartCountSelector = ({ CartPage }) => CartPage.cartCount;
export const addToCartInProgressSelector = ({ CartPage }) => CartPage.addToCartInProgress;
export const addToCartErrorSelector = ({ CartPage }) => CartPage.addToCartError;
export const currentPageResultIdsSelector = ({ CartPage }) => CartPage.currentPageResultIds;
export const currentCartItemIdSelector = ({ CartPage }) => CartPage.currentCartItemId;

// Action creators
const addToCartRequest = data => ({ type: ActionTypes.ADD_TO_CART_REQUEST, payload: data });
const addToCartSuccess = data => ({ type: ActionTypes.ADD_TO_CART_SUCCESS, payload: data });
const addToCartError = error => ({
  type: ActionTypes.ADD_TO_CART_ERROR,
  error: true,
  payload: error,
});
const setCart = data => ({ type: ActionTypes.SET_CART, payload: data });
const updateCartCount = data => ({ type: ActionTypes.UPDATE_CART_COUNT, payload: data });
const fetchListingRequest = () => ({ type: ActionTypes.FETCH_LISTING_REQUEST });
const fetchListingSuccess = response => ({
  type: ActionTypes.FETCH_LISTING_SUCCESS,
  payload: { data: response.data },
});
const updateCurrentPageResultIds = response => ({
  type: ActionTypes.UPDATE_CURRENT_PAGE_RESULT_IDS,
  payload: response,
});
const fetchListingError = error => ({
  type: ActionTypes.FETCH_LISTING_ERROR,
  error: true,
  payload: error,
});
const removeItemFromCartRequest = id => ({
  type: ActionTypes.REMOVE_ITEM_FROM_CART_REQUEST,
  payload: id,
});
const unsetRemovingItemInProgress = id => ({
  type: ActionTypes.UNSET_REMOVING_ITEM_IN_PROGRESS,
  payload: id,
});
const removeItemFromCartSuccess = () => ({ type: ActionTypes.REMOVE_ITEM_FROM_CART_SUCCESS });
const removeItemFromCartError = error => ({
  type: ActionTypes.REMOVE_ITEM_FROM_CART_ERROR,
  error: true,
  payload: error,
});

// Thunks
export const addItemToCart = ({ id, cartItems = [], cartItemsWithSize = [], history, routeConfiguration, size }) => async (
  dispatch,
  getState,
  sdk
) => {
  const { isAuthenticated } = getState().auth;
  dispatch(addToCartRequest(id));

  try {
    if (isAuthenticated) {
      // Only add the id if it's not already in cartItems
      if (!cartItems.includes(id)) {
        const updatedCartItems = [...cartItems.flat(Infinity), id];

        const updatedCartItemsWithSize = [
          ...cartItemsWithSize,
          {
            listingId: id,
            size: size ?? null,
          },
        ];

        try {
          await dispatch(
            updateProfile({
              protectedData: {
                cartItems: updatedCartItems,
                cartItemsWithSize: updatedCartItemsWithSize,
              },
            })
          );
          dispatch(addToCartSuccess(id));
          dispatch(updateCartCount(updatedCartItems.length));
          await dispatch(fetchCurrentUser());
        } catch (updateError) {
          // Revert
          const revertedCartItems = updatedCartItems.filter(item => item !== id);
          dispatch(setCart(revertedCartItems));
          dispatch(updateCartCount(revertedCartItems.length));
          dispatch(addToCartError(storableError(updateError)));
        }
      } else {
        // If id is already in cart, dispatch success without updating
        dispatch(addToCartSuccess(id));
      }
    } else {
      // For unauthenticated users, save cart and redirect to login
      saveCartAndRedirectToLogin({
        id,
        history,
        routeConfiguration,
      });
      dispatch(addToCartSuccess(id));
    }
  } catch (error) {
    dispatch(addToCartError(storableError(error)));
  }
};

export const deleteItemFromCart = params => async (dispatch, getState, sdk) => {
  const {
    id,
    isAuthenticated,
    cartItems,
    cartItemsWithSize = [],
  } = params;

  dispatch(removeItemFromCartRequest(id));

  const { currentPageResultIds } = getState().CartPage;

  const updatedCurrentPageResultIds = currentPageResultIds.filter(
    item => item?.uuid !== id
  );

  const flatCartItems = Array.isArray(cartItems)
    ? cartItems.flat(Infinity)
    : [];

  const updatedCartItems = flatCartItems.filter(item => item !== id);

  const updatedCartItemsWithSize = cartItemsWithSize.filter(
    item => item?.listingId !== id
  );

  const oldCartItems = [...flatCartItems];
  const oldCartItemsWithSize = [...cartItemsWithSize];
  const oldCurrentPageResultIds = [...currentPageResultIds];

  if (isAuthenticated) {
    // Optimistic update
    try {
      await dispatch(
        updateProfile({
          protectedData: {
            cartItems: updatedCartItems,
            cartItemsWithSize: updatedCartItemsWithSize,
          },
        })
      );

      dispatch(setCart(updatedCartItems));
      dispatch(updateCartCount(updatedCartItems.length));
      dispatch(updateCurrentPageResultIds(updatedCurrentPageResultIds));
      dispatch(removeItemFromCartSuccess());

      await dispatch(fetchCurrentUser());
    } catch (updateError) {
      // Revert optimistic update
      dispatch(setCart(oldCartItems));
      dispatch(updateCartCount(oldCartItems.length));
      dispatch(updateCurrentPageResultIds(oldCurrentPageResultIds));

      dispatch(
        removeItemFromCartError(storableError(updateError))
      );

      return;
    }
  } else {
    const updatedCartItemsLocal = removeItemFromLocalCart(id);

    dispatch(setCart(updatedCartItemsLocal));
    dispatch(updateCartCount(updatedCartItemsLocal.length));
    dispatch(updateCurrentPageResultIds(updatedCurrentPageResultIds));
    dispatch(removeItemFromCartSuccess());
  }
};

export const updateCartCountAction = () => (dispatch, getState) => {
  const { cart } = getState().CartPage;
  dispatch(updateCartCount(cart.length));
};

export const fetchListings = (params, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingRequest());
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const aspectRatio = aspectHeight / aspectWidth;

  try {
    const response = await sdk.listings.query({
      ids: params,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.listing': ['title', 'geolocation', 'price', 'description', 'publicData'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,

        // Scaled variants for large images
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',

        // Cropped variants for listing thumbnail images
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,

        // Social media
        'variants.facebook',
        'variants.twitter',

        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),

      'limit.images': 1,
    });
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchListingSuccess(response));
    return response;
  } catch (error) {
    dispatch(fetchListingError(storableError(error)));
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser || {};
  const cartItems = getUserCartDetails(currentUser);
  if (isArrayLength(cartItems)) {
    dispatch(setCart(cartItems));
    dispatch(updateCartCount(cartItems.length));
    dispatch(fetchListings(cartItems, config));
  }
  return currentUser;
};

export default cartPageReducer;
